import {useEffect, useState} from 'react'
import {
  Breadcrumb,
  Button,
  ButtonGroup,
  Col,
  Dropdown,
  Form,
  InputGroup,
  ListGroup,
  Modal,
  Row,
  Spinner,
} from 'react-bootstrap'
import Select from 'react-select'
import {useDisclourse} from '../../hooks/useDisclourse'
import {Tooltip} from 'react-tooltip'
import {ModalAddEditContact} from './components/ModalAddEditContact'
import {ModalCommunicationLogs} from './components/ModalCommunicationLogs'
import {SendMessageCanvas} from '../../components/messaging/SendMessageCanvas'
import {useLazyQuery, useMutation, useApolloClient, useQuery} from '@apollo/client'
import {GET_CONTACTS_PAGE_API} from '../../gql/queries/contactsApiQueries'
import {GET_ALL_POLICIES_STATUSES} from '../../gql/queries/policiesQueries'
import {GET_ALL_CARRIERS_LIST} from '../../gql/queries/carriersQuery'
import {GET_ALL_SIGNATURE_FORMS_ACTIVE} from '../../gql/queries/signatureFormsApiQueries'
import {GET_ALL_STATES_EU_LIST}  from '../../gql/queries/statesEUQuery'
import {GET_ALL_USERS_BASIC} from '../../gql/queries/usersQuery'
import {INITIAL_CONTACT_FILTERS, IMMIGRATION_STATUS_CHOICES} from '../../helpers/consts'
import {toast} from 'react-toastify'
import {IResContactsAPI, NodeContact} from '../../types/contactsApi'
import moment from 'moment'
import {SIZE_PAGE_DEFAULT} from '../../helpers/consts'
import {PaginationUtils} from '../shared/PaginationUtils'
import {DELETE_CONTACT, EDIT_CONTACT} from '../../gql/mutations/contactsMutation'
import {IResCreateContactApi} from '../../types/contacts'
import {PaginationSection} from '../shared/PaginationSection'
import {ModalPreview} from './components/ModalPreview'
import {ModalRequestSignature} from '../signatureForms/components/ModalRequestSignature'
import {ModalAddPolice} from '../policies/components/ModalAddPolice'
import {ModalFilters} from './components/ModalFilters'
import {ContactFilters, ContactSavedFilter} from './contactsTypes'
import {
  hasPermission,
  isAdministrator,
  parseId,
} from '../../../../app/tfi/helpers'
import {ALL_PERMISSIONS} from '../../../../app/tfi/helpers/consts'
import {useGetUser} from '../../../../app/tfi/store/userStore'
import {useStaticCatalog} from '../../hooks/useStaticCatalog'
import {GET_ALL_ORGANIZATIONS} from '../../gql/queries/organizationsQuery'
import {GET_MY_CONTACT_FILTERS} from '../../gql/queries/contactFiltersQueries'
import {
  CREATE_CONTACT_FILTER,
  DELETE_CONTACT_FILTER,
  UPDATE_CONTACT_FILTER,
} from '../../gql/mutations/contactFilterMutations'
import {PhoneProviderActions} from '../../components/telephony/PhoneProviderActions'

export const useLoadFilterData = () => {
  const permissions = useGetUser((user) => user?.user?.permissions) ?? []
  const client = useApolloClient()

  const fetchUsers = () =>
    client
      .query({query: GET_ALL_USERS_BASIC, fetchPolicy: 'network-only'})
      .then((res) => res.data)
  const usersCatalog = useStaticCatalog('contacts-users-basic', true, fetchUsers)

  const fetchSignatureForms = () =>
    client
      .query({query: GET_ALL_SIGNATURE_FORMS_ACTIVE, fetchPolicy: 'network-only'})
      .then((res) => res.data)
  const signatureFormsCatalog = useStaticCatalog('signature-forms-active', true, fetchSignatureForms)

  const fetchCarriers = () =>
    client
      .query({query: GET_ALL_CARRIERS_LIST, fetchPolicy: 'network-only'})
      .then((res) => res.data)
  const carriersCatalog = useStaticCatalog('carriers-list', true, fetchCarriers)

  const fetchStates = () =>
    client
      .query({query: GET_ALL_STATES_EU_LIST, fetchPolicy: 'network-only'})
      .then((res) => res.data)
  const statesCatalog = useStaticCatalog('states-eu-list', true, fetchStates)

  const fetchPolicyStatuses = () =>
    client
      .query({query: GET_ALL_POLICIES_STATUSES, fetchPolicy: 'network-only'})
      .then((res) => res.data)
  const canViewAllPolicyStatuses = hasPermission(permissions, ALL_PERMISSIONS.VIEW_POLICY_STATUS)
  const policyStatusesCatalog = useStaticCatalog(
    `policy-statuses:${canViewAllPolicyStatuses ? 'all' : 'agents'}`,
    true,
    fetchPolicyStatuses
  )

  if (usersCatalog.error) toast.error('Error loading users')
  if (signatureFormsCatalog.error) toast.error('Error loading signature forms')
  if (carriersCatalog.error) toast.error('Error loading carriers')
  if (statesCatalog.error) toast.error('Error loading states')
  if (policyStatusesCatalog.error) toast.error('Error loading policy statuses')

  return {
    dataUsers: usersCatalog.data,
    dataSignatureForms: signatureFormsCatalog.data,
    dataCarriers: carriersCatalog.data,
    dataPolicyStatuses: policyStatusesCatalog.data,
    dataStates: statesCatalog.data,
    loadingUsers: usersCatalog.loading,
    loadingSignatureForms: signatureFormsCatalog.loading,
    loadingCarriers: carriersCatalog.loading,
    loadingStates: statesCatalog.loading,
    loadingPolicyStatuses: policyStatusesCatalog.loading,
  }
}

export function useLocalStorage<T>(key: string, initialValue: T) {
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key)
      return item ? (JSON.parse(item) as T) : initialValue
    } catch {
      return initialValue
    }
  })

  useEffect(() => {
    try {
      window.localStorage.setItem(key, JSON.stringify(storedValue))
    } catch {
      // puede fallar en modo incógnito
    }
  }, [key, storedValue])

  return [storedValue, setStoredValue] as const
}



export const ContactsPage = () => {
  const permissions = useGetUser((user) => user?.user?.permissions) ?? []
  const userGroups = useGetUser((s) => s.user?.groups) || []
  const isSuperAdmin = userGroups.includes('SuperAdministrador')
  const canViewContacts = hasPermission(permissions, ALL_PERMISSIONS.VIEW_CONTACT)
  const canViewSignatureForms = hasPermission(permissions, ALL_PERMISSIONS.VIEW_SIGNATURE_FORMS)
  const canViewCommunications = hasPermission(permissions, ALL_PERMISSIONS.VIEW_COMMUNICATION_LOGS)

  const selectMenuPortalTarget =
    typeof document !== 'undefined' ? document.body : undefined

  
  const [gqlGetContacts, {data: dataAllContacts, loading, error, refetch}] =
    useLazyQuery<IResContactsAPI>(GET_CONTACTS_PAGE_API, {
      fetchPolicy: 'no-cache',
    })

  const [gqlCreateP, {data: dataUpdate, loading: loadingUpdate, error: errorUpdate}] =
    useMutation<IResCreateContactApi>(EDIT_CONTACT)
  const [gqlDeleteContact, {loading: loadingDeleteContact}] = useMutation(DELETE_CONTACT)
  const {data: dataContactFilters, refetch: refetchContactFilters} = useQuery<{
    myContactFilters: {
      id: string
      name: string
      filters: string | ContactFilters
      created?: string
      updated?: string
    }[]
  }>(GET_MY_CONTACT_FILTERS, {
    fetchPolicy: 'network-only',
  })
  const [gqlCreateContactFilter] = useMutation(CREATE_CONTACT_FILTER)
  const [gqlUpdateContactFilter] = useMutation(UPDATE_CONTACT_FILTER)
  const [gqlDeleteContactFilter] = useMutation(DELETE_CONTACT_FILTER)

  const [filtersContacts, setFiltersContacts] = useState<
    {id: string; name: string; icon: string}[]
  >([])

  const {isOpen: isOpenFilters, onOpen: onOpenFilters, onClose: onCloseFilters} = useDisclourse()
  const {
    dataUsers,
    dataSignatureForms,
    dataCarriers,
    dataPolicyStatuses,
    dataStates,
  } = useLoadFilterData()

  const filterData = {
    users: dataUsers?.allUsers?.edges.map(({node}) => ({
      ...node,
      fullName: `${node.firstName} ${node.lastName}`,
    })) || [],
    states: dataStates?.allStatesList || [],
    immigrationStatuses: IMMIGRATION_STATUS_CHOICES,
    signatureForms: dataSignatureForms?.allSignatureForms?.edges.map(({node}) => node) || [],
    carriers: dataCarriers?.allInsuranceOperatorsList || [],
    policyStatuses:
      dataPolicyStatuses?.allPolicyStatuses?.edges.map(({node}) => ({
        id: node.id,
        status: node.code,
      })) || [],
  }

  
  const [showAside, setShowAside] = useState(false)
  const {isOpen, onClose, onOpen} = useDisclourse()
  const {isOpen: isOpenPreview, onClose: onClosePreview, onOpen: onOpenPreview} = useDisclourse()
  const {isOpen: isOpenRequest, onClose: onCloseRequest, onOpen: onOpenRequest} = useDisclourse()
  const {isOpen: isOpenQuote, onClose: onCloseQuote, onOpen: onOpenQuote} = useDisclourse()
  const {
    isOpen: isOpenCommunications,
    onOpen: onOpenCommunications,
    onClose: onCloseCommunications,
  } = useDisclourse()
  const {
    isOpen: isOpenMessaging,
    onOpen: onOpenMessaging,
    onClose: onCloseMessaging,
  } = useDisclourse()


  const [tabFilters, setTabFilters] = useState('Contacts')
  const [contactToDelete, setContactToDelete] = useState<NodeContact | null>(null)

  const [infoContact, setInfoContact] = useState<{hasEmail: boolean; hasPhone: boolean}>()
  const [idContact, setIdContact] = useState<string>()
  const [infoPreview, setInfoPreview] = useState<null | NodeContact>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [offset, setOffset] = useState(0)
  const [first, setFirst] = useState(SIZE_PAGE_DEFAULT)

  const [isPag, setIsPag] = useState(false)
  const [page, setPage] = useState(1)

  const [dataArr, setDataArr] = useState<IResContactsAPI['allContacts']['edges']>([])

  let totalContacts = dataAllContacts?.allContacts?.totalCount ?? 0

  const totalPages = Math.ceil(totalContacts / first)
  const {start, end} = PaginationUtils.getPaginationRange(currentPage, first, totalContacts)

  const [formData, setFormData] = useLocalStorage<ContactFilters>(
    'contacts-filters',
    {...INITIAL_CONTACT_FILTERS}
  )
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string>(INITIAL_CONTACT_FILTERS.organizationId || '')

  const parseContactFilterPayload = (rawFilters: string | ContactFilters | undefined) => {
    if (!rawFilters) return INITIAL_CONTACT_FILTERS
    if (typeof rawFilters === 'string') {
      try {
        const parsed = JSON.parse(rawFilters) as ContactFilters
        return {...INITIAL_CONTACT_FILTERS, ...parsed}
      } catch {
        return INITIAL_CONTACT_FILTERS
      }
    }
    return {...INITIAL_CONTACT_FILTERS, ...rawFilters}
  }

  const savedContactFilters: ContactSavedFilter[] =
    dataContactFilters?.myContactFilters?.map((filter) => ({
      id: filter.id,
      name: filter.name,
      created: filter.created,
      updated: filter.updated,
      filters: parseContactFilterPayload(filter.filters),
    })) || []

  const {
    data: dataOrganizations,
    loading: loadingOrganizations,
    error: errorOrganizations,
  } = useQuery(GET_ALL_ORGANIZATIONS, {
    variables: {first: 100},
    fetchPolicy: 'no-cache',
    skip: !isSuperAdmin,
  })

  const handleSelectChange = (field: string, selected: any) => {
    let value = selected?.value
    if (value === 'true') value = true
    else if (value === 'false') value = false
    else if (value === '') value = undefined

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }



  const cleanVariables = (vars: any) => {
    return Object.fromEntries(
      Object.entries(vars).filter(([_, v]) => v !== '' && v !== null && v !== undefined)
    )
  }

  const serializeImmigrationStatus = (value: ContactFilters['immigrationStatus']) => {
    if (Array.isArray(value)) {
      const normalized = value.map((item) => String(item).trim()).filter(Boolean)
      return normalized.length ? normalized.join(',') : undefined
    }

    if (typeof value === 'string') {
      const normalized = value.trim()
      return normalized || undefined
    }

    return undefined
  }

  const handleContactFilterErrors = (
    errors: {field?: string; message?: string}[] | undefined,
    fallback: string
  ) => {
    const message = errors?.[0]?.message || fallback
    toast.error(message)
  }

  const handleSaveContactFilter = async (name: string, filters: ContactFilters) => {
    const trimmedName = name.trim()
    if (!trimmedName) {
      toast.error('Filter name is required.')
      return null
    }

    const result = await gqlCreateContactFilter({
      variables: {
        contactFilterData: {
          name: trimmedName,
          filters: JSON.stringify(filters),
        },
      },
    })

    const payload = result.data?.createContactFilter
    if (!payload?.success) {
      handleContactFilterErrors(payload?.errors, 'Unable to save filter.')
      return null
    }

    await refetchContactFilters()
    toast.success('Filter saved.')

    if (!payload.contactFilter) return null
    return {
      id: payload.contactFilter.id,
      name: payload.contactFilter.name,
      filters: parseContactFilterPayload(payload.contactFilter.filters),
      updated: payload.contactFilter.updated,
    } as ContactSavedFilter
  }

  const handleUpdateContactFilter = async (
    id: string,
    name: string,
    filters: ContactFilters
  ) => {
    const trimmedName = name.trim()
    if (!trimmedName) {
      toast.error('Filter name is required.')
      return null
    }

    const result = await gqlUpdateContactFilter({
      variables: {
        id: parseId(id),
        contactFilterData: {
          name: trimmedName,
          filters: JSON.stringify(filters),
        },
      },
    })

    const payload = result.data?.updateContactFilter
    if (!payload?.success) {
      handleContactFilterErrors(payload?.errors, 'Unable to update filter.')
      return null
    }

    await refetchContactFilters()
    toast.success('Filter updated.')

    if (!payload.contactFilter) return null
    return {
      id: payload.contactFilter.id,
      name: payload.contactFilter.name,
      filters: parseContactFilterPayload(payload.contactFilter.filters),
      updated: payload.contactFilter.updated,
    } as ContactSavedFilter
  }

  const handleDeleteContactFilter = async (id: string) => {
    const result = await gqlDeleteContactFilter({
      variables: {id: parseId(id)},
    })

    const payload = result.data?.deleteContactFilter
    if (!payload?.success) {
      handleContactFilterErrors(payload?.errors, 'Unable to delete filter.')
      return false
    }

    await refetchContactFilters()
    toast.success('Filter deleted.')
    return true
  }

  const handleFilters = () => {
    const filtersToSend = { ...formData }

    // ❌ No enviar si está en false
    if (filtersToSend.isPolicyHolder === false) {
      delete filtersToSend.isPolicyHolder
    }

    const variables = cleanVariables({
      ...filtersToSend,
      immigrationStatus: serializeImmigrationStatus(filtersToSend.immigrationStatus),
      isImportant: tabFilters === 'Important' ? true : formData.isImportant,
      withoutLead: tabFilters === 'Leads' ? false : formData.withoutLead,
      user: formData.user ? parseId(formData.user) : null,
      assignedTo: formData.assignedTo ? parseId(formData.assignedTo) : null,
      organization: isSuperAdmin && formData.organizationId ? parseId(formData.organizationId) : null,
      first,
      offset: (currentPage - 1) * first,
    })

    gqlGetContacts({ variables })
  }


  const handleFilterApply = (filters: any) => {
    const transformedFilters = {...filters}

    if (filters.signatureStatus === 'SIGNED') {
      transformedFilters.hasSignedEntry = true
    } else if (filters.signatureStatus === 'NOT_SIGNED') {
      transformedFilters.hasSignedEntry = false
    }

    delete transformedFilters.signatureStatus

    const newFormData = {
      ...formData,
      ...transformedFilters,
    }

    // ❌ No enviar si está en false
    if (newFormData.isPolicyHolder === false) {
      delete newFormData.isPolicyHolder
    }

    setFormData(newFormData)

    const variables = cleanVariables({
      ...newFormData,
      immigrationStatus: serializeImmigrationStatus(newFormData.immigrationStatus),
      isImportant: tabFilters === 'Important' ? true : newFormData.isImportant,
      withoutLead: tabFilters === 'Leads' ? false : newFormData.withoutLead,
      user: newFormData.user ? parseId(newFormData.user) : null,
      assignedTo: newFormData.assignedTo ? parseId(newFormData.assignedTo) : null,
      organization: isSuperAdmin && newFormData.organizationId ? parseId(newFormData.organizationId) : null,
      first,
      offset: (currentPage - 1) * first,
    })
    gqlGetContacts({ variables })
  }

  useEffect(() => {
    handleFilters()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [first, gqlGetContacts, currentPage, tabFilters])

  useEffect(() => {
    if (errorOrganizations) {
      toast.error(`Error: ${errorOrganizations.message}`)
    }
  }, [errorOrganizations])

  const handleContactName = () => {
    handleFilters()
  }

  useEffect(() => {
    if (!dataAllContacts) return
    const {edges} = dataAllContacts.allContacts
    // if (edges.length === 0) return
    if (isPag) {
      setDataArr((p) => [...p, ...edges])
    } else {
      setDataArr(edges)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataAllContacts])

  useEffect(() => {
    if (error) {
      toast.error(`Error: ${error.message}`)
    }
  }, [error])

  const updatePage = (n: number) => {
    setCurrentPage(n)
  }

  const handleUpdateContact = async (id: string, data: any) => {
    gqlCreateP({
      variables: {
        id: parseId(id),
        contactData: data,
      },
    })
  }

  const handleDeleteContact = async (id: string) => {
    const result = await gqlDeleteContact({
      variables: {id: parseId(id)},
    })

    const payload = result.data?.deleteContact
    if (!payload?.success) {
      toast.error(payload?.errors?.[0]?.message || 'Unable to delete contact.')
      return
    }

    toast.success('Contact deleted.')
    setContactToDelete(null)
    await handleFilters()
  }

  // if ((loading || !dataAllContacts) && !isPag) {
  //   return <LoadingContacts />
  // }

  if (!canViewContacts) {
    return (
      <div className='policies-page'>
        <header className='header'>
          <Breadcrumb>
            <Breadcrumb.Item className='fs-3'>Contacts</Breadcrumb.Item>
          </Breadcrumb>
        </header>
        <div className='d-flex flex-center' style={{height: '40vh'}}>
          <h4>You don't have permission to view Contacts.</h4>
        </div>
      </div>
    )
  }

  return (
    <div className='policies-page'>
      <header className='header'>
        <Breadcrumb>
          <Breadcrumb.Item className='d-blok d-md-none' onClick={() => setShowAside(true)}>
            <span>
              <i className='bi bi-list fs-1' />
            </span>
          </Breadcrumb.Item>
          <Breadcrumb.Item className='fs-3'>Contacts</Breadcrumb.Item>
          {/* <Breadcrumb.Item active>Quote </Breadcrumb.Item> */}
        </Breadcrumb>

        <div className='d-flex align-items-center gap-2'>
          <Button
            size='sm'
            onClick={() => {
              setIdContact('')
              onOpen()
            }}
          >
            <i className='bi bi-plus fs-3' /> New Contact
          </Button>
        </div>
      </header>
      <Row className='policies'>
        <Col md={3} lg={3} sm={0} xl={3} className='menu'>
          <div className='menu-aside'>
            <div className='title'>
              <h5 style={{fontSize: '13px'}}>Menu</h5>
            </div>
            <div className='list-menu'>
              <ListGroup variant='flush'>
                {/* All Contacts */}
                <ListGroup.Item
                  action
                  variant={tabFilters === 'AllContacts' ? 'primary' : 'light'}
                  onClick={() => {
                    setTabFilters('AllContacts')
                    setFormData(INITIAL_CONTACT_FILTERS) // sin filtros especiales
                  }}
                >
                  <i className='bi bi-person-fill fs-3' style={{color: 'inherit'}} /> All Contacts
                </ListGroup.Item>

                {/* Leads */}
                <ListGroup.Item
                  action
                  variant={tabFilters === 'Leads' ? 'primary' : 'light'}
                  onClick={() => {
                    setTabFilters('Leads')
                    setFormData((prev) => ({
                      ...INITIAL_CONTACT_FILTERS,
                      withoutLead: false, // solo con lead asociado
                    }))
                  }}
                >
                  <i className='bi bi-currency-dollar fs-3' style={{color: 'inherit'}} /> Leads
                </ListGroup.Item>

                {/* Favoritos guardados dinámicos */}
                {filtersContacts.length > 0 &&
                  filtersContacts.map((lis) => (
                    <ListGroup.Item
                      key={lis.name}
                      className='filters-favorites'
                      action
                      variant={tabFilters === lis.id ? 'primary' : 'light'}
                      onClick={() => {
                        setTabFilters(lis.id)
                        setFormData((prev) => ({...prev, insuranceCategory: lis.id}))
                      }}
                    >
                      {lis.icon && (
                        <i
                          className={`bi bi-${lis.icon} fs-3`}
                          style={{color: 'inherit', marginRight: '8px'}}
                        />
                      )}
                      {lis.name}
                    </ListGroup.Item>
                  ))}

                {/* Important */}
                <ListGroup.Item
                  action
                  variant={tabFilters === 'Important' ? 'primary' : 'light'}
                  onClick={() => setTabFilters('Important')}
                >
                  <i className='bi bi-star-fill fs-3' style={{color: 'inherit'}} /> Important
                </ListGroup.Item>
              </ListGroup>
            </div>

            {/* <div
              style={{
                borderBottom: '2px solid rgba(93, 120, 255, 0.2)',
              }}
              className='d-flex align-items-center justify-content-between'
            >
              <h5 className='m-0 p-0'>Agency Folders</h5>
              <Button variant='link'>
                <i className='bi bi-pencil-square' style={{color: 'inherit'}} /> Manage
              </Button>
            </div>
            <p className='my-4'>Your agency has no folders.</p>
            <div
              style={{
                borderBottom: '2px solid rgba(93, 120, 255, 0.2)',
              }}
              className='d-flex align-items-center justify-content-between mt-5'
            >
              <h5 className='m-0 p-0'>
                Agency Folders
                <span>
                  <span data-tooltip-id='asdf'>
                    <i className='bi bi-info-circle-fill fs-5 ms-1' style={{color: '#5867dd'}} />
                  </span>
                  <Tooltip
                    id='asdf'
                    place='right'
                    style={{
                      backgroundColor: 'white',
                      color: 'black',
                      boxShadow: '2px 2px 2px 1px rgba(0, 0, 0, 0.2)',
                      opacity: 1,
                    }}
                  >
                    <div className='tooltip-tfi'>
                      <h6 className='text-center'>English</h6>
                      <p className='text-muted'>
                        Only policies created by you can be stored in your personal folders.
                      </p>
                      <h6 className='text-center'>Español</h6>
                      <p className='text-muted'>
                        Una dirección de facturación es la dirección relacionada con una forma de
                        pago específica.
                      </p>
                    </div>
                  </Tooltip>
                </span>
              </h5>
              <Button variant='link'>
                <i className='bi bi-pencil-square' style={{color: 'inherit'}} /> Manage
              </Button>
            </div>
            <p className='my-4'>You have no personal folders.</p> */}
          </div>
        </Col>
        <Col md={8} lg={8} sm={12} xl={9} className='container'>
          <div className='policies-container'>
            <div className='title-header'>
              <h3>All Contacts</h3>
            </div>
            <div className='filters-header'>
              <InputGroup>
                <Form.Control
                  placeholder='Type your query here...'
                  aria-label="Contact's username with two button addons"
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault() // evita el submit automático
                      handleContactName() // si tienes una función como esa
                    }
                  }}
                  value={formData.name}
                />
                {isSuperAdmin && (
                  <Form.Select
                    value={formData.organizationId}
                    onChange={(e) => {
                      const value = e.target.value
                      setFormData((prev) => ({...prev, organizationId: value}))
                      setCurrentPage(1)
                    }}
                    disabled={loadingOrganizations}
                  >
                    {loadingOrganizations ? (
                      <option value=''>Loading...</option>
                    ) : (
                      <option value=''>All Organizations</option>
                    )}
                    {dataOrganizations?.allOrganizations?.edges?.map(({node}: any) => (
                      <option key={node.id} value={parseId(node.id)}>
                        {node.name}
                      </option>
                    ))}
                  </Form.Select>
                )}
                <Button
                  onClick={() => handleFilters()}
                  variant='outline-success'
                  style={{border: '1px solid #dbdfe9'}}
                >
                  Search
                </Button>
                <Button
                  variant='outline-primary'
                  style={{border: '1px solid #dbdfe9'}}
                  onClick={onOpenFilters}
                >
                  Filter
                </Button>
              </InputGroup>
              <div className='d-flex align-items-center gap-4 flex-wrap'>
                <Form.Check
                  type='checkbox'
                  id='policyholders-only'
                  checked={formData.isPolicyHolder ?? false}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      isPolicyHolder: e.target.checked,
                    }))
                  }
                  label={
                    <span>
                      Show policyholders only{' '}
                      <span className='ms-2' data-tooltip-id='tooltip-isPolicyHolder'>
                        <i className='bi bi-question-circle-fill text-primary' />
                      </span>
                      <Tooltip
                        id='tooltip-isPolicyHolder'
                        place='bottom'
                        style={{
                          zIndex: 99999,
                          backgroundColor: 'white',
                          color: 'black',
                          boxShadow:
                            'rgba(0, 0, 0, 0.12) 0px 1px 3px, rgba(0, 0, 0, 0.24) 0px 1px 2px',
                        }}
                      >
                        <div className='tooltip-tfi'>
                          <p style={{color: '#222'}}>
                            Check this box if you want to show only contacts who are policyholders.
                          </p>
                        </div>
                      </Tooltip>
                    </span>
                  }
                />

                <Form.Label>Has Policy</Form.Label>
                <Select
                  menuPortalTarget={selectMenuPortalTarget}
                  menuPosition='fixed'
                  styles={{
                    menuPortal: (base) => ({...base, zIndex: 9999}),
                  }}
                  value={
                    formData.withoutPolicy === true
                      ? { value: 'true', label: 'No' }
                      : formData.withoutPolicy === false
                      ? { value: 'false', label: 'Yes' }
                      : { value: '', label: 'All' }
                  }
                  options={[
                    { value: '', label: 'All' },
                    { value: 'false', label: 'Yes' },
                    { value: 'true', label: 'No' },
                  ]}
                  onChange={(selected) => handleSelectChange('withoutPolicy', selected)}
                />

                

                <Form.Check
                  type='checkbox'
                  id='my-contacts'
                  checked={formData.myContacts ?? false}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      myContacts: e.target.checked,
                    }))
                  }
                  label={
                    <span>
                      Show my contacts only{' '}
                      <span className='ms-2' data-tooltip-id='tooltip-myContacts'>
                        <i className='bi bi-question-circle-fill text-primary' />
                      </span>
                      <Tooltip
                        id='tooltip-myContacts'
                        place='bottom'
                        style={{
                          zIndex: 99999,
                          backgroundColor: 'white',
                          color: 'black',
                          boxShadow:
                            'rgba(0, 0, 0, 0.12) 0px 1px 3px, rgba(0, 0, 0, 0.24) 0px 1px 2px',
                        }}
                      >
                        <div className='tooltip-tfi'>
                          <p style={{color: '#222'}}>Show only the contacts created by you.</p>
                        </div>
                      </Tooltip>
                    </span>
                  }
                />
              </div>
            </div>
            <div className='results-header'>
              <PaginationSection
                first={first}
                setFirst={(n) => {
                  setFirst(n)
                  setPage(1)
                  setOffset(0)
                }}
                currentPage={currentPage}
                totalPages={totalPages}
                start={start}
                end={end}
                totalPolicies={totalContacts}
                updatePage={updatePage}
                PaginationUtils={PaginationUtils}
              />
              <div className='col-md-3'>
                <div className='results-filter'>
                  <Col lg={12} md={12} sm={12} xs={12}></Col>
                </div>
              </div>
            </div>

            <div className='table-responsive table table-striped table-hover '>
              <table
                className='table-scrollable table-scroll  table table-striped table-hover '
                style={{whiteSpace: 'nowrap'}}
              >
                <thead>
                  <tr role='row'>
                    <th>
                      {/* <input
                        className='form-check-input'
                        type='checkbox'
                        checked={isAllSelected}
                        onChange={toggleAll}
                      /> */}
                    </th>
                    <th className='fw-bold'>Contact information</th>
                    <th className='fw-bold'>Phone</th>
                    <th className='fw-bold'>Email</th>
                    <th className='fw-bold'>Date of Birth</th>
                    <th className='fw-bold'>Created</th>
                    <th className='fw-bold'>Actions</th>
                  </tr>
                </thead>
                {(loading || !dataAllContacts) && !isPag ? (
                  <tbody>
                    <tr>
                      <td colSpan={8}>
                        <div className='d-flex flex-center' style={{height: '30vh'}}>
                          <Spinner animation='border' />
                        </div>
                      </td>
                    </tr>
                  </tbody>
                ) : (
                  <tbody>
                    {dataArr.length === 0 && (
                      <tr>
                        <td colSpan={8}>
                          <div className='d-flex flex-center' style={{height: '30vh'}}>
                            There are no contacts currently added
                          </div>
                        </td>
                      </tr>
                    )}
                    {loading && (
                      <tr>
                        <td colSpan={8}>
                          <div className='d-flex flex-center' style={{height: '30vh'}}>
                            <Spinner animation='border' />
                          </div>
                        </td>
                      </tr>
                    )}
                    {dataArr.slice(first * page - first, first * page).map(({node}) => {
                      const entries = node.signatureEntries?.edges || []
                      const hasSigned = entries.some((e) => e.node.status === 'SIGNED')
                      const hasRequested = entries.some((e) => !!e.node.requestedOn)
                      const hasMembers = (node.members?.edges?.length || 0) > 0
                      const isDeletable = !node.lead && !node.memberPolicy && !hasMembers && entries.length === 0

                      return (
                        <tr
                          key={node.id}
                          onClick={() => {
                            setInfoPreview(node)
                            onOpenPreview()
                          }}
                          style={{cursor: 'pointer'}}
                        >
                          <td>
                            <div className='set-quick-status'>
                              <div className='set-important'>
                                <i
                                  style={{
                                    color: node.isImportant ? 'gold' : 'inherit',
                                    cursor: 'pointer',
                                  }}
                                  className='bi bi-star-fill fs-3'
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    node.isImportant = !node.isImportant
                                    handleUpdateContact(node.id, {isImportant: node.isImportant})
                                  }}
                                />
                              </div>
                              <div>
                                {hasSigned ? (
                                  <i
                                    style={{color: 'blue', cursor: 'pointer'}}
                                    className='bi bi-vector-pen fs-4'
                                    title='Consent request signed'
                                  />
                                ) : hasRequested ? (
                                  <i
                                    style={{color: 'gray', cursor: 'pointer'}}
                                    className='bi bi-send-check fs-4'
                                    title='Consent request sent'
                                  />
                                ) : null}
                              </div>
                            </div>
                          </td>
                          <td>
                            <div>
                              <span
                                className='text-primary text-link fw-bold'
                                style={{cursor: 'pointer'}}
                                data-tooltip-id={`myt2${parseId(node.id)}`}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setInfoPreview(node)
                                  onOpenPreview()
                                }}
                              >
                                {node.fullName}
                              </span>
                            </div>
                          </td>
                          <td>
                            <PhoneProviderActions phone={node.phone} />
                          </td>
                          <td>{node.email}</td>
                          <td>
                            {node.dateBirth ? (
                              <>
                                <div>{moment(node.dateBirth).format('MM/DD/YYYY')}</div>
                                <div className='text-muted' style={{fontSize: '0.85rem'}}>
                                  {moment().diff(moment(node.dateBirth), 'years')} years
                                </div>
                              </>
                            ) : (
                              'N/A'
                            )}
                          </td>
                          <td>{moment(node.created).format('MM/DD/YYYY')}</td>
                          <td>
                            <Dropdown as={ButtonGroup} onClick={(e) => e.stopPropagation()}>
                              <Button
                                variant='outline-primary'
                                size='sm'
                                style={{border: '1px solid #3e97ff'}}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  onOpenPreview()
                                  setInfoPreview(node)
                                }}
                              >
                                Preview
                              </Button>
                              <Dropdown.Toggle
                                split
                                variant='outline-primary'
                                size='sm'
                                id='dropdown-custom-2'
                                style={{border: '1px solid #3e97ff'}}
                              />
                              <Dropdown.Menu className='dropdown-menu-policy'>
                                <Dropdown.Item
                                  eventKey='1'
                                  onClick={() => {
                                    setIdContact(node.id)
                                    onOpen()
                                  }}
                                >
                                  <i className='bi bi-pencil-fill' /> Edit
                                </Dropdown.Item>

                                {canViewCommunications && (
                                  <Dropdown.Item
                                    onClick={() => {
                                      setInfoPreview(node)
                                      onOpenCommunications()
                                    }}
                                  >
                                    <i className='bi bi-chat-left-text' /> Communication Logs
                                  </Dropdown.Item>
                                )}

                                {hasPermission(permissions, ALL_PERMISSIONS.VIEW_SMS) && (
                                  <Dropdown.Item
                                    onClick={() => {
                                      setInfoPreview(node)
                                      onOpenMessaging()
                                    }}
                                  >
                                    <i className='bi bi-whatsapp' /> Send Notification
                                  </Dropdown.Item>
                                )}

                                {canViewSignatureForms && (
                                  hasSigned ? (
                                    <Dropdown.Item
                                      eventKey='2'
                                      onClick={() => {
                                        setIdContact(node.id)
                                        onOpenQuote()
                                      }}
                                    >
                                      <i className='bi bi-file-earmark-plus' /> Create quote
                                    </Dropdown.Item>
                                  ) : (
                                    <Dropdown.Item
                                      eventKey='2'
                                      onClick={() => {
                                        setIdContact(node.id)
                                        setInfoContact({
                                          hasEmail: !!node.email,
                                          hasPhone: !!node.phone,
                                        })
                                        onOpenRequest()
                                      }}
                                    >
                                      <i className='bi bi-send-check' /> Request Signature
                                    </Dropdown.Item>
                                  )
                                )}

                                {isDeletable && (
                                  <>
                                    <Dropdown.Divider />
                                    <Dropdown.Item
                                      eventKey='3'
                                      onClick={() => {
                                        setContactToDelete(node)
                                      }}
                                    >
                                      <i className='bi bi-trash' /> Delete
                                    </Dropdown.Item>
                                  </>
                                )}
                              </Dropdown.Menu>
                            </Dropdown>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                )}
              </table>
            </div>
          </div>
        </Col>
      </Row>

      {isOpen && (
        <ModalAddEditContact
          refetch={refetch}
          isOpen={isOpen}
          onClose={onClose}
          keyboard={false}
          backdrop='static'
          idContact={idContact}
        />
      )}
      {isOpenPreview && infoPreview && (
        <ModalPreview idContact={infoPreview.id} onClose={onClosePreview} />
      )}
      {isOpenRequest && idContact && canViewSignatureForms && (
        <ModalRequestSignature
          keyboard={false}
          backdrop='static'
          isOpen={isOpenRequest}
          sourceId={idContact}
          sourceType='contact'
          onClose={onCloseRequest}
          refetch={refetch}
          hasEmail={infoContact?.hasEmail}
          hasPhone={infoContact?.hasPhone}
        />
      )}

      {isOpenQuote && idContact && (
        <ModalAddPolice
          refetch={refetch}
          isOpen={isOpenQuote}
          onClose={onCloseQuote}
          keyboard={false}
          backdrop='static'
          isQuote={true}
          idContact={idContact}
        />
      )}

      {isOpenCommunications && infoPreview && (
        <ModalCommunicationLogs
          idContact={infoPreview.id}
          isOpen={isOpenCommunications}
          onClose={onCloseCommunications}
        />
      )}

      {isOpenMessaging && infoPreview && (
        <SendMessageCanvas
          show={isOpenMessaging}
          onHide={onCloseMessaging}
          recipient={{
            name: (infoPreview.fullName || '').trim(),
            phone: infoPreview.phone || '',
            email: infoPreview.email || '',
          }}
          context={{
            type: 'CONTACT',
            id: infoPreview.id,
          }}
          data={{
            contact: infoPreview,
            lead: {
              ...infoPreview,
              name: infoPreview.fullName,
              first_name: infoPreview.firstName,
              last_name: infoPreview.lastName,
            },
          }}
          defaultChannel="WHATSAPP"
        />
      )}

      <Modal show={!!contactToDelete} onHide={() => setContactToDelete(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete contact</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <h4 className='fs-2 mb-0'>
            Are you sure you want to delete this contact?
          </h4>
          {contactToDelete?.fullName && (
            <p className='mt-3 mb-0 text-muted'>
              {contactToDelete.fullName}
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant='secondary'
            size='sm'
            onClick={() => setContactToDelete(null)}
            disabled={loadingDeleteContact}
          >
            Cancel
          </Button>
          <Button
            variant='danger'
            size='sm'
            onClick={() => {
              if (!contactToDelete?.id) return
              void handleDeleteContact(contactToDelete.id)
            }}
            disabled={loadingDeleteContact}
          >
            {loadingDeleteContact && <Spinner animation='border' size='sm' className='me-2' />}
            Delete
          </Button>
        </Modal.Footer>
      </Modal>


      {isOpenFilters && (
         <ModalFilters
          isOpen={isOpenFilters}
          onClose={onCloseFilters}
          onApply={handleFilterApply}
          filters={formData}
          setFilters={setFormData}
          savedFilters={savedContactFilters}
          onSaveFilter={handleSaveContactFilter}
          onUpdateFilter={handleUpdateContactFilter}
          onDeleteFilter={handleDeleteContactFilter}
          {...filterData}
        />

      )}
    </div>
  )
}
